import React from 'react';
import { Project } from '../../types/project';
import { PROJECT_PRIORITY_META, ProjectStatusSelect, ProjectAvatars, fmtShort, isProjectOverdue, daysLeft } from './projectMeta';
import { CalendarIcon, BuildingOfficeIcon, PencilSquareIcon, TrashIcon, ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, FolderIcon } from '@heroicons/react/24/outline';

interface Props {
  projects: Project[];
  onOpen: (p: Project) => void;
  onEdit: (p: Project) => void;
  onDelete: (id: string) => void;
  onUpdateStatus: (id: string, s: Project['status']) => void;
  taskCounts?: Record<string, { total: number; done: number; open: number }>;
}

const ProjectCards: React.FC<Props> = ({ projects, onOpen, onEdit, onDelete, onUpdateStatus, taskCounts = {} }) => {
  if (projects.length === 0) {
    return (
      <div className="crm-card px-4 py-14 text-center">
        <FolderIcon className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
        <div className="text-sm text-gray-500 dark:text-gray-400">Nincs a szűrésnek megfelelő projekt</div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {projects.map(p => {
        const pr = PROJECT_PRIORITY_META[p.priority] || PROJECT_PRIORITY_META.medium;
        const overdue = isProjectOverdue(p);
        const left = daysLeft(p);
        const tc = taskCounts[p.id];
        const progress = tc && tc.total > 0 ? Math.round((tc.done / tc.total) * 100) : (p.progress || 0);
        return (
          <div
            key={p.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(p)}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p); }}
            className="crm-card overflow-hidden cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 transition-colors flex flex-col"
          >
            <div className="h-1" style={{ backgroundColor: p.color || '#0f7f75' }} />
            <div className="p-4 flex-1">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ backgroundColor: `${p.color || '#0f7f75'}1a`, color: p.color || '#0f7f75' }}>
                  {p.icon || (p.key || p.name).slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">{p.name}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">{p.key}</div>
                    </div>
                    <ProjectStatusSelect value={p.status} onChange={(s) => onUpdateStatus(p.id, s)} size="xs" />
                  </div>
                </div>
              </div>
              {p.description && <p className="mt-2 text-xs text-gray-600 dark:text-gray-300 line-clamp-2">{p.description.replace(/<[^>]+>/g, '')}</p>}

              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-1 tabular-nums">
                  <span>{tc ? `${tc.done}/${tc.total} feladat kész` : 'Haladás'}</span>
                  <span className="font-medium text-gray-700 dark:text-gray-200">{progress}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div className={`h-full rounded-full ${progress >= 100 ? 'bg-emerald-500' : 'bg-primary-600'}`} style={{ width: `${Math.min(100, progress)}%` }} />
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                <span className={`inline-flex items-center gap-1 ${pr.text}`}><span className={`w-1.5 h-1.5 rounded-full ${pr.dot}`} />{pr.label}</span>
                <span className={`inline-flex items-center gap-1 tabular-nums ${overdue ? 'text-rose-600 dark:text-rose-400' : ''}`}>
                  <CalendarIcon className="w-3 h-3" />{fmtShort(p.startDate)} – {fmtShort(p.endDate)}
                  {overdue ? <ExclamationTriangleIcon className="w-3 h-3" /> : left !== null && left >= 0 && left <= 7 && p.status === 'active' ? <span className="text-amber-600 dark:text-amber-400">· {left} nap</span> : null}
                </span>
                {p.customerName && <span className="inline-flex items-center gap-1 truncate max-w-[160px]"><BuildingOfficeIcon className="w-3 h-3" />{p.customerName}</span>}
              </div>
            </div>
            <div className="px-4 py-2 border-t border-[var(--crm-border)] flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
              <ProjectAvatars names={p.assignedUsersNames} />
              <div className="flex items-center gap-0.5 text-gray-500 dark:text-gray-400">
                <button type="button" onClick={() => onOpen(p)} className="p-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white" title="Megnyitás"><ArrowTopRightOnSquareIcon className="w-4 h-4" /></button>
                <button type="button" onClick={() => onEdit(p)} className="p-1.5 rounded-md hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-white" title="Szerkesztés"><PencilSquareIcon className="w-4 h-4" /></button>
                <button type="button" onClick={() => onDelete(p.id)} className="p-1.5 rounded-md hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/20" title="Törlés"><TrashIcon className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectCards;
